<script setup lang="ts">
import { computed } from 'vue';
import type { PropType } from 'vue';
import type { BrandingPreviewState } from '../utils/branding-preview-channel';

const props = defineProps({
  /**
   * The unsaved form state, bound as a plain property (`:state`). The host rebuilds it as a fresh
   * object on every edit, so Vue patches `el.state` and the element re-renders — a custom element
   * needs no message channel to stay in sync.
   */
  state: {
    type: Object as PropType<BrandingPreviewState | null>,
    default: null,
  },
});

/**
 * Mirrors the `sendMailForSendPayment` SES template. Branding comes from the unsaved form;
 * payment details are samples and the collection link stays inside the preview.
 */
const SAMPLE_SUBJECT = 'You got paid';
const SAMPLE_TO = 'recipient@example.com';
const SAMPLE_FROM = 'notification-no-reply@snaprefund.io';
const SAMPLE_AMOUNT = '1,250.00';
const SAMPLE_MEMO = 'Invoice #4821 — October services';
const COPYRIGHT_YEAR = new Date().getFullYear();
/** The email template uses green when `primaryColor` is absent. */
const EMAIL_DEFAULT_PRIMARY_COLOR = '#1e8841';
/** `snapLogoImageUrl`: the platform's own logo, shown whatever the branding looks like. */
const SNAP_LOGO_URL = '/images/Logo-snapRefund.png';

/** `username` in the template: the name the recipient knows the sender by. */
const businessName = computed(() => props.state?.businessName || props.state?.profileName || 'Your business');
/** `logoImageUrl` — falls back to the platform logo exactly like the backend does. */
const logoUrl = computed(() => props.state?.logoUrl || SNAP_LOGO_URL);
/** `primaryColor` — paints the collection button; the amount is always green. */
const primaryColor = computed(() => props.state?.primaryColor || EMAIL_DEFAULT_PRIMARY_COLOR);
</script>

<template>
  <!-- The chrome a mail client shows above the body: this pane is a message, not a page. -->
  <div class="email-preview">
    <div class="email-preview__chrome">
      <p class="email-preview__meta">
        <span class="email-preview__meta-key">From</span>
        <span class="email-preview__meta-value">{{ SAMPLE_FROM }}</span>
      </p>
      <p class="email-preview__meta">
        <span class="email-preview__meta-key">Subject</span>
        <span class="email-preview__meta-value">{{ SAMPLE_SUBJECT }}</span>
      </p>
      <p class="email-preview__meta">
        <span class="email-preview__meta-key">To</span>
        <span class="email-preview__meta-value">{{ SAMPLE_TO }}</span>
      </p>
    </div>

    <div class="email-preview__body">
      <div class="email-preview__header">
        <img class="email-preview__logo" :src="logoUrl" :alt="businessName" />
      </div>
      <div class="email-preview__content">
        <h2 class="email-preview__headline">Collect Your Payment</h2>
        <hr />
        <h3 class="email-preview__line">
          <strong>{{ businessName }}</strong> has sent you a payment of
          <strong class="email-preview__amount">${{ SAMPLE_AMOUNT }}</strong
          >!
        </h3>
        <h3 class="email-preview__line"><strong>Memo:</strong> {{ SAMPLE_MEMO }}</h3>
        <h3 class="email-preview__line">
          Click the button below to choose your preferred payment method and cash out.
        </h3>
        <div class="email-preview__action">
          <a
            href="#"
            class="email-preview__collect"
            :style="{ backgroundColor: primaryColor, borderColor: primaryColor }"
            @click.prevent
          >
            Collect Payment
          </a>
        </div>
      </div>
      <div class="email-preview__footer">
        <h5 class="email-preview__rights">© {{ COPYRIGHT_YEAR }} All rights reserved, SnapRefund, Inc.</h5>
        <table width="100%" cellpadding="0" cellspacing="0">
          <tbody>
            <tr>
              <td class="email-preview__footer-logo-cell">
                <img class="email-preview__snap-logo" :src="SNAP_LOGO_URL" alt="SnapRefund" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<!--
  `.ce.vue` is custom-element mode (`@vitejs/plugin-vue` default): this block is not extracted into
  the page but handed to `defineCustomElement`, which injects it into the shadow root. That is the
  whole point of the pane — the app's stylesheet cannot reach the replica (bootstrap resets and
  Raleway would turn an email into a web app), and none of this leaks back out into the modal.
-->
<style>
:host {
  display: block;
  height: 100%;
  overflow: auto;
  background: #f8fafc;
  color: #1f2328;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.45;
}

.email-preview__chrome {
  padding: 12px 16px;
  border-bottom: 1px solid #d9dee5;
  background: #ffffff;
}

.email-preview__meta {
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 2px 0;
  font-size: 13px;
}

.email-preview__meta-key {
  flex: 0 0 52px;
  color: #7b8491;
}

.email-preview__meta-value {
  font-weight: 600;
  word-break: break-all;
}

.email-preview__body {
  padding: 32px;
  background: #f8fafc;
  color: #333333;
  font-family: 'Raleway', Arial, sans-serif;
}

.email-preview__header {
  padding: 24px 32px;
}

.email-preview__logo {
  display: block;
  width: 100%;
  max-width: 400px;
  margin: auto;
}

.email-preview__content {
  padding: 32px;
  border-radius: 4px 4px 0 0;
  background: #ffffff;
}

.email-preview__headline {
  margin: 0 0 16px;
  font-size: 24px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
}

.email-preview__line {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.email-preview__line strong {
  font-weight: 700;
}

.email-preview__amount {
  color: #1e8841;
}

.email-preview__action {
  text-align: center;
}

.email-preview__collect {
  display: inline-block;
  width: 100%;
  max-width: 320px;
  margin-top: 30px;
  border: 1px solid;
  border-radius: 8px;
  color: #ffffff;
  font-size: 26px;
  font-weight: bold;
  line-height: 70px;
  text-align: center;
}

.email-preview__footer {
  padding: 32px 16px;
}

.email-preview__footer-logo-cell {
  width: 50%;
  text-align: left;
  vertical-align: middle;
}

.email-preview__snap-logo {
  display: inline-block;
  width: 100%;
  max-width: 240px;
  margin: 0;
}

.email-preview__rights {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
}
</style>
